import React from "react";
import { motion } from "framer-motion";
import {
  CalendarClock, UserCheck, Home, Scale, ShieldCheck,
  Check, CheckCircle2, Sparkles, Zap, Search, Layers,
} from "lucide-react";
import { fadeUp } from "./anim";

// 5 categories · 38 risk indicators — a deliberately deep, proprietary analysis.
const CATEGORIES = [
  {
    key: "booking",
    title: "Booking Intelligence",
    icon: CalendarClock,
    tone: "primary",
    items: [
      "Booking lead time",
      "Same-day booking signals",
      "Last-minute booking behaviour",
      "Length of stay analysis",
      "Group size vs property capacity",
      "Occupancy risk indicators",
      "Local guest indicators",
      "High-season booking patterns",
    ],
  },
  {
    key: "trust",
    title: "Guest Trust Signals",
    icon: UserCheck,
    tone: "low",
    items: [
      "Reviews & rating quality",
      "Review consistency analysis",
      "Account age signals",
      "Verification indicators",
      "Communication quality",
      "Communication responsiveness",
      "Profile completeness signals",
      "Booking history indicators",
    ],
  },
  {
    key: "property",
    title: "Property Exposure Analysis",
    icon: Home,
    tone: "medium",
    items: [
      "Property type & value",
      "Estimated damage exposure",
      "Event suitability risk",
      "High-value asset exposure",
      "Smoking risk indicators",
      "Party risk indicators",
      "Noise complaint risk",
      "House rule compliance signals",
    ],
  },
  {
    key: "financial",
    title: "Financial & Dispute Risk",
    icon: Scale,
    tone: "high",
    items: [
      "Refund dispute indicators",
      "Fraud risk signals",
      "Chargeback likelihood",
      "Compensation request patterns",
      "Claim dispute indicators",
      "Coercion risk indicators",
      "Extortion risk indicators",
      "Review manipulation indicators",
    ],
  },
  {
    key: "protection",
    title: "Host Protection Analysis",
    icon: ShieldCheck,
    tone: "primary",
    items: [
      "Damage claim exposure",
      "Review dispute exposure",
      "Communication breakdown risk",
      "Evidence strength indicators",
      "Escalation risk indicators",
      "Host liability indicators",
    ],
  },
];

const TRUST = [
  "Analyses 30+ booking and risk indicators",
  "Assessment completed in seconds",
  "Built specifically for Airbnb and short-term rental hosts",
  "Identifies risk patterns hosts may miss",
  "Designed to support stronger hosting decisions",
];

// Subtle, semantic icon-chip tones using existing palette tokens.
const TONE = {
  primary: { chip: "bg-primary/10 text-primary", dot: "text-primary" },
  low: { chip: "bg-[hsl(var(--risk-low-bg))] text-[hsl(var(--risk-low))]", dot: "text-[hsl(var(--risk-low))]" },
  medium: { chip: "bg-[hsl(var(--risk-medium-bg))] text-[hsl(var(--risk-medium))]", dot: "text-[hsl(var(--risk-medium))]" },
  high: { chip: "bg-[hsl(var(--risk-high-bg))] text-[hsl(var(--risk-high))]", dot: "text-[hsl(var(--risk-high))]" },
};

export const RiskFactors = () => (
  <section id="factors" className="border-y bg-secondary/30">
    <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
      {/* Header */}
      <motion.div {...fadeUp} className="mx-auto max-w-3xl text-center">
        <span
          className="inline-flex items-center gap-1.5 rounded-full border border-primary/15 bg-primary/5 px-3 py-1 text-xs font-medium text-primary"
          data-testid="risk-factors-badge"
        >
          <Sparkles className="h-3.5 w-3.5" /> Proprietary risk engine · 30+ indicators
        </span>
        <h2 className="mt-4 font-display text-3xl font-semibold tracking-tight sm:text-4xl" data-testid="risk-factors-heading">
          Risk factors we assess
        </h2>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground" data-testid="risk-factors-intro">
          HostGuard analyses dozens of booking, behavioural and dispute-related risk indicators to identify
          potential issues before they become expensive problems. Every assessment generates detailed
          category-specific risk scores across party, damage, smoking, fraud, refund, coercion, extortion and
          communication risk—helping hosts make informed decisions before accepting a reservation.
        </p>
      </motion.div>

      {/* Premium trust row */}
      <motion.ul
        {...fadeUp}
        transition={{ ...fadeUp.transition, delay: 0.08 }}
        className="mx-auto mt-8 flex max-w-4xl flex-wrap items-center justify-center gap-x-6 gap-y-2.5"
        data-testid="risk-factors-trust"
      >
        {TRUST.map((t) => (
          <li key={t} className="flex items-center gap-2 text-sm font-medium text-foreground">
            <CheckCircle2 className="h-4 w-4 shrink-0 text-[hsl(var(--risk-low))]" />
            {t}
          </li>
        ))}
      </motion.ul>

      {/* Category grid */}
      <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="risk-factors-grid">
        {CATEGORIES.map((c, i) => {
          const tone = TONE[c.tone];
          const Icon = c.icon;
          return (
            <motion.div
              key={c.key}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.06 * i }}
              className="group rounded-2xl border bg-card p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-[0_14px_34px_rgba(15,23,42,0.10)]"
              data-testid={`risk-category-${c.key}`}
            >
              <div className="flex items-center gap-3">
                <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${tone.chip}`}>
                  <Icon className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="font-display text-base font-semibold leading-tight">{c.title}</h3>
                  <p className="text-xs text-muted-foreground">{c.items.length} indicators</p>
                </div>
              </div>
              <ul className="mt-5 space-y-2.5">
                {c.items.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                    <Check className={`mt-0.5 h-4 w-4 shrink-0 ${tone.dot}`} />
                    <span className="leading-snug">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          );
        })}

        {/* Capability highlight tile to balance the 6th grid cell */}
        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.06 * CATEGORIES.length }}
          className="flex flex-col justify-center gap-4 rounded-2xl border border-dashed border-primary/20 bg-primary/[0.03] p-6"
          data-testid="risk-factors-capability"
        >
          {[
            { icon: Layers, title: "5 risk dimensions", desc: "Booking, trust, property, financial and protection signals combined." },
            { icon: Search, title: "Deeper than a profile", desc: "Behavioural and dispute patterns most hosts can't check manually." },
            { icon: Zap, title: "Seconds, not hours", desc: "A repeatable, professional analysis on every reservation." },
          ].map((b) => (
            <div key={b.title} className="flex items-start gap-3">
              <b.icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div>
                <p className="text-sm font-semibold text-foreground">{b.title}</p>
                <p className="text-xs leading-relaxed text-muted-foreground">{b.desc}</p>
              </div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Why HostGuard Is Different — premium highlighted card */}
      <motion.div
        {...fadeUp}
        transition={{ ...fadeUp.transition, delay: 0.1 }}
        className="relative mt-10 overflow-hidden rounded-2xl bg-primary p-8 text-primary-foreground shadow-[0_20px_50px_rgba(15,23,42,0.20)] sm:p-10"
        data-testid="why-different-card"
      >
        {/* subtle decorative accent (kept well under 20% of the card) */}
        <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-primary-foreground/5 blur-2xl" aria-hidden="true" />
        <div className="relative max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary-foreground/10 px-3 py-1 text-xs font-medium text-primary-foreground/90">
            <ShieldCheck className="h-3.5 w-3.5" /> Host protection, by design
          </span>
          <h3 className="mt-4 font-display text-2xl font-semibold tracking-tight sm:text-3xl">
            Why HostGuard Is Different
          </h3>
          <p className="mt-4 leading-relaxed text-primary-foreground/85">
            Airbnb displays booking information. HostGuard analyses risk indicators, behavioural patterns and
            dispute-related signals to help hosts identify potential issues before they become costly problems.
          </p>
          <p className="mt-3 leading-relaxed text-primary-foreground/85">
            HostGuard was designed specifically for short-term rental hosting and combines booking intelligence,
            property exposure analysis and dispute-prevention insights into a single assessment.
          </p>
        </div>
      </motion.div>
    </div>
  </section>
);
